import { z } from 'zod';
import { emailSchema } from './auth';
import { paginatedSchema, paginationSchema, normalizedUuidSchema as uuidSchema } from './common';

// ---------------------------------------------------------------------------
// 알림 모듈 1단계(SUPER-80) — 알림 종류·발송 상태·건너뜀 사유와 관리자 발송 이력·재발송·알림 설정 계약.
// DB enum(NotificationKind·NotificationDeliveryStatus·NotificationTargetType)의 값 원본은 @repo/database(Prisma)다.
// 이 패키지는 @repo/database 에 의존하지 않으므로(브라우저 진입점) 두 정의의 일치는 api 스펙
// `apps/api/src/common/__test__/notification-kinds-contract.spec.ts` 가 고정한다.
// 항상-발송 종류(`NOTIFICATION_ALWAYS_ON_KINDS`)는 이 패키지가 유일한 TS 원본이다(DB 에는 CHECK 제약만 있다).
// ---------------------------------------------------------------------------

/** 알림 종류 17개(설계 §3 순서) — 기록·ON/OFF·재발송의 단위는 실제 메일 하나다(N2). */
export const NOTIFICATION_KINDS = [
  'AUTH_EMAIL_VERIFICATION',
  'AUTH_PASSWORD_RESET',
  'AUTH_EMAIL_CHANGE',
  'QUOTE_NEW_REQUEST_ADMIN',
  'QUOTE_GUEST_RECEIVED',
  'QUOTE_GUEST_ISSUED',
  'ORDER_CONFIRMED',
  'ORDER_SHIPPED',
  'ORDER_DELAYED',
  'ORDER_SUPPLY_FAILED',
  'ORDER_REFUNDED',
  'ORDER_CANCEL_REQUESTED_ADMIN',
  'ORDER_INQUIRY_RECEIVED_ADMIN',
  'ORDER_INQUIRY_ANSWERED',
  'ORDER_DEPOSIT_REQUESTED',
  'ORDER_DEPOSIT_EXPIRED',
  'TAX_INVOICE_FAILED_ADMIN',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];
export const notificationKindSchema = z.enum(NOTIFICATION_KINDS);

/** 관리자 화면의 종류 이름. */
export const NOTIFICATION_KIND_LABELS: Readonly<Record<NotificationKind, string>> = {
  AUTH_EMAIL_VERIFICATION: '이메일 인증',
  AUTH_PASSWORD_RESET: '비밀번호 재설정',
  AUTH_EMAIL_CHANGE: '이메일 변경 확인',
  QUOTE_NEW_REQUEST_ADMIN: '새 견적 요청(운영자)',
  QUOTE_GUEST_RECEIVED: '비회원 접수 확인',
  QUOTE_GUEST_ISSUED: '비회원 견적서 발송',
  ORDER_CONFIRMED: '결제 완료',
  ORDER_SHIPPED: '발송 안내',
  ORDER_DELAYED: '배송 지연',
  ORDER_SUPPLY_FAILED: '수급 실패',
  ORDER_REFUNDED: '환불 완료',
  ORDER_CANCEL_REQUESTED_ADMIN: '주문 취소 요청(운영자)',
  ORDER_INQUIRY_RECEIVED_ADMIN: '교환·환불 문의 접수(운영자)',
  ORDER_INQUIRY_ANSWERED: '교환·환불 문의 답변',
  ORDER_DEPOSIT_REQUESTED: '입금 안내',
  ORDER_DEPOSIT_EXPIRED: '입금 기한 만료 안내',
  TAX_INVOICE_FAILED_ADMIN: '세금계산서 발행 실패(운영자)',
};

export interface NotificationKindGroup {
  readonly label: string;
  readonly kinds: readonly NotificationKind[];
}

/**
 * 관리자 화면의 표시 묶음 — 와이어프레임의 거친 유형명(N2). 알림 설정의 11행 순서이자 이력 필터의 묶음 라벨이다.
 * 종류마다 정확히 한 묶음에 들어간다.
 */
export const NOTIFICATION_KIND_GROUPS: ReadonlyArray<NotificationKindGroup> = [
  { label: '계정', kinds: ['AUTH_EMAIL_VERIFICATION', 'AUTH_PASSWORD_RESET', 'AUTH_EMAIL_CHANGE'] },
  { label: '상담 접수', kinds: ['QUOTE_NEW_REQUEST_ADMIN', 'QUOTE_GUEST_RECEIVED'] },
  { label: '견적서 발송', kinds: ['QUOTE_GUEST_ISSUED'] },
  { label: '결제 완료', kinds: ['ORDER_CONFIRMED', 'ORDER_REFUNDED'] },
  { label: '배송 상태', kinds: ['ORDER_SHIPPED'] },
  {
    label: '주문 상태',
    kinds: [
      'ORDER_DELAYED',
      'ORDER_SUPPLY_FAILED',
      'ORDER_CANCEL_REQUESTED_ADMIN',
      'ORDER_INQUIRY_RECEIVED_ADMIN',
      'ORDER_INQUIRY_ANSWERED',
      'ORDER_DEPOSIT_REQUESTED',
      'ORDER_DEPOSIT_EXPIRED',
      'TAX_INVOICE_FAILED_ADMIN',
    ],
  },
];

/**
 * 항상 발송(스위치 없음, N3) — 인증 3종은 끄면 가입·재설정·이메일 변경이 막히고, 결제 완료·환불 완료는 전자상거래법상 고객 고지 의무다.
 * DB 의 `notification_kind_settings` CHECK(chk_notification_kind_settings_toggleable)가 같은 5종을 SQL 로 막는다.
 */
export const NOTIFICATION_ALWAYS_ON_KINDS = [
  'AUTH_EMAIL_VERIFICATION',
  'AUTH_PASSWORD_RESET',
  'AUTH_EMAIL_CHANGE',
  'ORDER_CONFIRMED',
  'ORDER_REFUNDED',
] as const satisfies readonly NotificationKind[];
export type NotificationAlwaysOnKind = (typeof NOTIFICATION_ALWAYS_ON_KINDS)[number];
const ALWAYS_ON_KINDS: ReadonlySet<NotificationKind> = new Set<NotificationKind>(
  NOTIFICATION_ALWAYS_ON_KINDS,
);
export const isNotificationKindAlwaysOn = (kind: NotificationKind): boolean =>
  ALWAYS_ON_KINDS.has(kind);

/** 관리자 재발송 가능 — 인증 3종은 토큰 원문을 저장하지 않아 다시 만들 수 없다(고객이 화면에서 다시 요청한다, N6·N7). */
export const NOTIFICATION_RESENDABLE_KINDS: readonly NotificationKind[] = NOTIFICATION_KINDS.filter(
  (kind) => !kind.startsWith('AUTH_'),
);
const RESENDABLE_KINDS: ReadonlySet<NotificationKind> = new Set<NotificationKind>(
  NOTIFICATION_RESENDABLE_KINDS,
);
export const isNotificationKindResendable = (kind: NotificationKind): boolean =>
  RESENDABLE_KINDS.has(kind);

export type NotificationAudience = 'OPERATOR' | 'CUSTOMER';
/** 받는 사람 — `*_ADMIN` 종류는 운영자 수신자(여러 명, 수신자마다 기록 행 하나, N4), 나머지는 고객 한 사람. */
export const notificationAudienceOf = (kind: NotificationKind): NotificationAudience =>
  kind.endsWith('_ADMIN') ? 'OPERATOR' : 'CUSTOMER';

/** 발송 상태 — QUEUED 는 "보낼 일"(outbox, N5), 나머지는 끝난 상태다. */
export const NOTIFICATION_DELIVERY_STATUSES = ['QUEUED', 'SENT', 'FAILED', 'SKIPPED'] as const;
export type NotificationDeliveryStatus = (typeof NOTIFICATION_DELIVERY_STATUSES)[number];
export const notificationDeliveryStatusSchema = z.enum(NOTIFICATION_DELIVERY_STATUSES);
export const NOTIFICATION_DELIVERY_STATUS_LABELS: Readonly<
  Record<NotificationDeliveryStatus, string>
> = {
  QUEUED: '대기',
  SENT: '성공',
  FAILED: '실패',
  SKIPPED: '건너뜀',
};

/** 기록 대상 — 인증은 회원, 견적 3종은 견적 요청, 주문 6종은 주문. 대상 id 에는 FK 가 없다(대상이 지워져도 기록은 남는다). */
export const NOTIFICATION_TARGET_TYPES = ['USER', 'QUOTE_REQUEST', 'ORDER'] as const;
export type NotificationTargetType = (typeof NOTIFICATION_TARGET_TYPES)[number];
export const notificationTargetTypeSchema = z.enum(NOTIFICATION_TARGET_TYPES);

/**
 * 건너뜀(SKIPPED) 사유 — `notification_deliveries.skip_reason` 에 이 코드를 그대로 남긴다. 로그·Sentry 에도 코드만 싣는다(N17).
 * 견적·주문 사유는 worker 렌더러(`QuoteEmailRenderer`·`OrderEmailRenderer`)의 "보내기 직전 상태 확인" 규칙이 남긴다 — 옛 메일 서비스의 규칙 그대로다(N12).
 */
export const NOTIFICATION_SKIP_REASONS = [
  // 알림 모듈 — 꺼진 종류(worker, N3)·운영자 수신자 없음(api 기록·브리지, N4)·주소 없음(api 기록)·탈퇴로 비운 주소(탈퇴 처리·worker)
  'KIND_DISABLED',
  'NO_OPERATOR_RECIPIENT',
  'RECIPIENT_MISSING',
  'RECIPIENT_WITHDRAWN',
  // 견적 메일 — 견적 없음·이메일 연락 비회원 아님·회원 계정에 연결됨·발행 상태 아님·최신 발행본 아님
  'QUOTE_NOT_FOUND',
  'NOT_EMAIL_GUEST',
  'CLAIMED',
  'QUOTE_NOT_ISSUED',
  'NOT_LATEST_ISSUED',
  // 주문 메일 — 주문·행위 행 없음·결제 완료 아님·환불 완료 아님·취소 요청 처리됨·남은 송장 없음·결제 없음·공급 불가 요청 아님
  'ORDER_OR_ACTION_NOT_FOUND',
  'ORDER_NOT_PAID',
  'ORDER_NOT_REFUNDED',
  'REQUEST_NOT_PENDING',
  'NO_REMAINING_SHIPMENTS',
  'PAYMENT_NOT_FOUND',
  'NOT_SUPPLY_FAILURE_REQUEST',
  // 교환·환불 문의 메일 — 답변 완료 상태가 아님
  'INQUIRY_NOT_ANSWERED',
] as const;
export type NotificationSkipReason = (typeof NOTIFICATION_SKIP_REASONS)[number];

/**
 * 실패(FAILED) 행의 `last_error` 에 코드 그대로 남는 실패. 둘 다 재시도해도 같은 결과라 worker 는 재시도 없이 닫는다.
 * - `SECRET_UNAVAILABLE` — 인증 메일의 토큰 원문을 다시 얻을 수 없다(조정 잡·발송, N6).
 * - `RENDERER_MISSING` — worker 에 그 종류의 렌더러가 배선되지 않았다(배포 구성 오류).
 */
export const NOTIFICATION_FAILURE_CODES = ['SECRET_UNAVAILABLE', 'RENDERER_MISSING'] as const;
export type NotificationFailureCode = (typeof NOTIFICATION_FAILURE_CODES)[number];

/** 이력 화면의 사유 한글 라벨 — 건너뜀 사유와 실패 코드. 값을 읽을 때는 `notificationSkipReasonLabel` 을 쓴다. */
export const NOTIFICATION_SKIP_REASON_LABELS: Readonly<Record<string, string>> = {
  KIND_DISABLED: '꺼진 알림',
  NO_OPERATOR_RECIPIENT: '운영자 수신자 없음',
  RECIPIENT_MISSING: '수신 주소 없음',
  RECIPIENT_WITHDRAWN: '탈퇴 회원',
  QUOTE_NOT_FOUND: '견적 없음',
  NOT_EMAIL_GUEST: '이메일 연락 비회원 아님',
  CLAIMED: '회원 계정에 연결됨',
  QUOTE_NOT_ISSUED: '발행 상태 아님',
  NOT_LATEST_ISSUED: '최신 발행본 아님',
  ORDER_OR_ACTION_NOT_FOUND: '주문·처리 기록 없음',
  ORDER_NOT_PAID: '결제 완료 상태 아님',
  ORDER_NOT_REFUNDED: '환불 완료 상태 아님',
  REQUEST_NOT_PENDING: '이미 처리된 취소 요청',
  NO_REMAINING_SHIPMENTS: '남은 송장 없음',
  PAYMENT_NOT_FOUND: '결제 기록 없음',
  NOT_SUPPLY_FAILURE_REQUEST: '공급 불가 요청 아님',
  INQUIRY_NOT_ANSWERED: '답변 완료 상태 아님',
  SECRET_UNAVAILABLE: '인증 토큰 없음',
  RENDERER_MISSING: '렌더러 없음',
} satisfies Record<NotificationSkipReason | NotificationFailureCode, string>;

const SKIP_REASON_LABEL_MAP: ReadonlyMap<string, string> = new Map(
  Object.entries(NOTIFICATION_SKIP_REASON_LABELS),
);

/** 사유 코드의 한글 라벨. 모르는 코드(다음 릴리스의 새 사유)는 코드를 그대로 돌려줘 빈칸이 되지 않게 한다. */
export const notificationSkipReasonLabel = (reason: string): string =>
  SKIP_REASON_LABEL_MAP.get(reason) ?? reason;

/** 운영자 수신자 최대 인원(N4) — 앱이 검사한다(DB 에는 상한이 없다). */
export const NOTIFICATION_OPERATOR_RECIPIENTS_MAX = 10;
/** `last_error` 저장 상한 — `redactMailError` 를 거친 문자열을 이 길이로 자른다. */
export const NOTIFICATION_LAST_ERROR_MAX_LENGTH = 500;

/**
 * 수신 주소(재발송 주소 바꾸기·운영자 수신자 추가·이력 검색) — 앞뒤 공백을 지우고 소문자로 바꾼 뒤 회원 이메일(`emailSchema`)과 같은
 * 규칙(형식·254자)으로 검사한다. DB CHECK(소문자·공백 없음·`@` 포함)를 앞에서 맞춘다.
 */
export const notificationRecipientEmailSchema = z.string().trim().toLowerCase().pipe(emailSchema);

/**
 * 발송 이력 목록 입력. 기간은 `created_at` 기준 `from ≤ created_at < to` 이고, 둘 다 `Z` 로 끝나는 ISO 일시만 받는다.
 * 수신 이메일은 정확 일치 검색(소문자)이라 검색할 때마다 `PII_ACCESSED` 감사가 남는다(N10). 비어 있으면 칸을 뺀다.
 */
export const notificationDeliveryListInputSchema = paginationSchema.extend({
  kind: notificationKindSchema.optional(),
  status: notificationDeliveryStatusSchema.optional(),
  recipientEmail: notificationRecipientEmailSchema.optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});
export type NotificationDeliveryListInput = z.infer<typeof notificationDeliveryListInputSchema>;

/** 참조 — 견적번호·주문번호 링크. 회원 대상(인증)이거나 대상이 지워졌으면 `label` 은 null 이다. */
export const notificationDeliveryReferenceSchema = z.strictObject({
  type: notificationTargetTypeSchema,
  id: uuidSchema,
  label: z.string().nullable(),
});
export type NotificationDeliveryReference = z.infer<typeof notificationDeliveryReferenceSchema>;

/** 발송 이력 한 행. 본문은 저장하지 않으므로 싣지 않는다(N8). 탈퇴로 비운 주소는 null 이고 `recipientRedacted` 가 true 다. */
export const notificationDeliveryItemSchema = z.strictObject({
  id: uuidSchema,
  kind: notificationKindSchema,
  channel: z.literal('EMAIL'),
  status: notificationDeliveryStatusSchema,
  recipientEmail: z.string().nullable(),
  recipientRedacted: z.boolean(),
  reference: notificationDeliveryReferenceSchema,
  /** 보낸 뒤 제목 — SENT 행만 있다. */
  subject: z.string().nullable(),
  skipReason: z.string().nullable(),
  /** 주소를 가린 실패 사유(최대 500자). */
  lastError: z.string().nullable(),
  attemptCount: z.number().int().nonnegative(),
  resendOfId: uuidSchema.nullable(),
  requestedByUserId: uuidSchema.nullable(),
  createdAt: z.iso.datetime(),
  /** SENT·FAILED·SKIPPED 시각. 대기(QUEUED)면 null. */
  finishedAt: z.iso.datetime().nullable(),
});
export type NotificationDeliveryItem = z.infer<typeof notificationDeliveryItemSchema>;
export const notificationDeliveryListSchema = paginatedSchema(notificationDeliveryItemSchema);
export type NotificationDeliveryList = z.infer<typeof notificationDeliveryListSchema>;

/**
 * 재발송(ADMIN, N7) — 원본 기록 id 와 바꿔 보낼 주소(선택). 주소가 없으면 원본 주소로 보낸다. 원본이 탈퇴로 비었고 주소도 없으면
 * `NOTIFICATION_RECIPIENT_REQUIRED` 다. 결과는 새로 만든 대기 행의 id 다.
 */
export const resendNotificationInputSchema = z.strictObject({
  deliveryId: uuidSchema,
  recipientEmail: notificationRecipientEmailSchema.optional(),
});
export type ResendNotificationInput = z.infer<typeof resendNotificationInputSchema>;
export const resendNotificationResultSchema = z.strictObject({ deliveryId: uuidSchema });
export type ResendNotificationResult = z.infer<typeof resendNotificationResultSchema>;

/** 종류별 ON/OFF — 행이 없으면 켜짐. 항상-발송 종류는 `alwaysOn` 이 true 이고 `enabled` 도 늘 true 다. */
export const notificationKindSettingSchema = z.strictObject({
  kind: notificationKindSchema,
  enabled: z.boolean(),
  alwaysOn: z.boolean(),
});
export type NotificationKindSetting = z.infer<typeof notificationKindSettingSchema>;

export const notificationOperatorRecipientSchema = z.strictObject({
  id: uuidSchema,
  email: z.string(),
  createdAt: z.iso.datetime(),
});
export type NotificationOperatorRecipient = z.infer<typeof notificationOperatorRecipientSchema>;

/**
 * 알림 설정 화면. `operatorFallbackConfigured` 는 운영자 수신자가 비었을 때 쓰는 환경 변수 기본값(`QUOTE_NOTIFY_EMAIL`)이 api 에
 * 설정돼 있는지다. 주소 자체는 내리지 않는다.
 */
export const notificationSettingsSchema = z.strictObject({
  kinds: z.array(notificationKindSettingSchema),
  operatorRecipients: z.array(notificationOperatorRecipientSchema),
  operatorFallbackConfigured: z.boolean(),
});
export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;

/** 종류 ON/OFF 바꾸기 — 항상-발송 종류는 스키마가 아니라 유스케이스가 `NOTIFICATION_KIND_ALWAYS_ON` 으로 거부한다. */
export const updateNotificationKindSettingInputSchema = z.strictObject({
  kind: notificationKindSchema,
  enabled: z.boolean(),
});
export type UpdateNotificationKindSettingInput = z.infer<
  typeof updateNotificationKindSettingInputSchema
>;

export const addNotificationOperatorRecipientInputSchema = z.strictObject({
  email: notificationRecipientEmailSchema,
});
export type AddNotificationOperatorRecipientInput = z.infer<
  typeof addNotificationOperatorRecipientInputSchema
>;

export const removeNotificationOperatorRecipientInputSchema = z.strictObject({ id: uuidSchema });
export type RemoveNotificationOperatorRecipientInput = z.infer<
  typeof removeNotificationOperatorRecipientInputSchema
>;
